import React, { useState } from "react";
import { TextInput } from "@narmi/design_system";

interface ExpiryErrorMessages {
  invalidDate: string;
  invalidMonth: string;
  datePassed: string;
}

const DEFAULT_ERROR_MESSAGES: ExpiryErrorMessages = {
  invalidDate: "Please enter a valid expiration date",
  invalidMonth: "Please enter a valid expiration month",
  datePassed: "Expiration date must be in the future",
};

export const validateCardExpiry = (
  value: string,
  errorMessages: ExpiryErrorMessages = DEFAULT_ERROR_MESSAGES
): string | null => {
  // validate that we have a month and year
  if (/^[0-9]{2}[0-9]{2}/.test(value)) {
    return errorMessages.invalidDate;
  }
  // validate that the month is between 01 and 12
  const month = parseInt(value.slice(0, 2));
  if (month < 1 || month > 12) {
    return errorMessages.invalidMonth;
  }

  const now = new Date();
  const expDate = new Date(value.replace("/", "/1/"));

  // a negative value in this expression indicates a month-year combo in the past
  if (expDate.getMonth() - now.getMonth() + 12 * (expDate.getFullYear() - now.getFullYear()) < 0) {
    return errorMessages.datePassed;
  }
  return null;
};

type ExpirationDateFieldProps = {
  onChange?: (value: string) => void;
  value?: string;
  /** Accepted for ContextForm.Field compatibility (injected via cloneElement); unused */
  field?: string;
  /** Accepted for ContextForm.Field compatibility (injected via cloneElement); unused */
  product?: unknown;
} & React.ComponentProps<typeof TextInput>;

const ExpirationDateField = ({
  onChange,
  value,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  field,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  product,
  ...textInputProps
}: ExpirationDateFieldProps) => {
  const [displayValue, setDisplayValue] = useState<string | undefined>("");
  const [moveCursor, setMoveCursor] = useState<(() => void) | undefined>();

  React.useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  React.useEffect(() => {
    if (moveCursor) moveCursor();
    setMoveCursor(undefined);
  }, [displayValue]);

  const handleInput = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // see if we're cursored to the middle of this field
    if (event.target.selectionStart !== event.target.value?.length) {
      // synthetic events are pooled, closure what we might need
      const target = event.target;
      const selectionStart = target.selectionStart;
      const selectionEnd = target.selectionEnd;
      setMoveCursor(() => () => {
        target.setSelectionRange(selectionStart, selectionEnd);
      });
    }

    // initially clean the parsedValue to just be numbers (e.g. remove the '/')
    const cleanedInput = event.target.value.replace(/\D/g, "");

    // whenever the just-digits length is 2 or more, add the '/' after the 2nd char
    let formattedInput = cleanedInput.replace(/^(..)(.?.)/, (_match, p1, p2) => [p1, p2].join("/"));

    // don't allow formattedInput to exceed 5 chars (incl. '/')
    formattedInput = formattedInput.slice(0, 5);

    if (formattedInput.length === 1 && !["0", "1"].includes(formattedInput))
      formattedInput = "0" + formattedInput;

    onChange?.(formattedInput);
  };

  return (
    <TextInput
      onChange={handleInput}
      value={displayValue}
      inputMode="numeric"
      autoComplete="cc-exp"
      {...textInputProps}
    />
  );
};

export default ExpirationDateField;
